import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';

// Fetch all admin users
export const fetchAdminUsers = createAsyncThunk(
  'adminUsers/fetchAdminUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getAdminUsers();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch admin users';
      return rejectWithValue(errorMessage);
    }
  }
);

// Create new admin user
export const createAdminUser = createAsyncThunk(
  'adminUsers/createAdminUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.createAdminUser(userData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create admin user';
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete admin user
export const deleteAdminUser = createAsyncThunk(
  'adminUsers/deleteAdminUser',
  async (userId, { rejectWithValue }) => {
    try {
      await api.deleteAdminUser(userId);
      return userId;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete admin user';
      return rejectWithValue(errorMessage);
    }
  }
);

// Reset admin password
export const resetAdminPassword = createAsyncThunk(
  'adminUsers/resetAdminPassword',
  async ({ userId, newPassword, confirmPassword }, { rejectWithValue }) => {
    try {
      await api.resetAdminPassword(userId, newPassword, confirmPassword);
      return { userId };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to reset password';
      return rejectWithValue(errorMessage);
    }
  }
);

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState: {
    users: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch admin users
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create admin user
      .addCase(createAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete admin user
      .addCase(deleteAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset password
      .addCase(resetAdminPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetAdminPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetAdminPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = adminUsersSlice.actions;
export default adminUsersSlice.reducer;

